/**
 * Report designer (obj_type "R" — Jasper JRXML reports).
 *
 * Tabs:
 *  - Design   — the EMBEDDED Jasper designer workspace (source / visual /
 *               query editors) consumed as SOURCE from the sibling
 *               jasper_report_designer.ai repo via the '@jasper' alias,
 *               lazy-loaded so its weight (monaco, xyflow, MUI …) only
 *               downloads when a report is opened.
 *  - Criteria — twasta-NATIVE criteria rows, one per JRXML $P{} parameter,
 *               rendered with the visual designer's OWN <CriteriaCard> (the
 *               shared component in visual/CriteriaCard.tsx) so report criteria
 *               are authored EXACTLY like visual criteria: collapsible cards
 *               with a summary line, prompt heading, data type, operator,
 *               token-aware defaults, IN chip lists, fixed dropdowns, F2 lookup
 *               and item_change.
 *               It runs the card in `promptOnly` mode: a report criterion
 *               always prompts, because its value binds a JRXML $P{} parameter
 *               (one rule → one parameter, report_params.fold_report_params) —
 *               so the visual-only "fixed value / system variable" source and
 *               the two-bound BETWEEN, which the WHERE-builder owns, are out.
 *  - Options  — default/allowed output formats, title, data connection.
 */
import {
  Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft, ChevronsDownUp, ChevronsUpDown, FileText, Loader2,
  Maximize2, Minimize2, Save, Upload,
} from 'lucide-react'
import type { EmbeddedDesignerProps } from './embeddedDesigner'
import { loadReportObject, saveReportObject } from '@/api/designers'
import ObjectSchedulesPanel from './scheduler/ObjectSchedulesPanel'
import { listProjectVariables, type ProjectVariable } from '@/api/projectVariables'
import { useProjectStore } from '@/store/projectStore'
import { useThemeStore } from '@/store/themeStore'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { useAgentRefresh } from '@/hooks/useAgentRefresh'
import DiscardChangesModal from '@/components/common/DiscardChangesModal'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'
import CriteriaCard from './visual/CriteriaCard'
import type { CriteriaRule } from './visual/types'
import { makeReportEmbedApi } from './report/embedApi'
import {
  REPORT_FORMAT_OPTIONS, blankReportModel, defaultReportJrxml,
  sanitizeReportModel, type ReportModel, type ReportOutputFormat,
} from './report/types'
// Shared with the workbench object node's own "Import JRXML" — one definition
// of what importing a report means (see report/importBundle.ts).
import { readPickedReportFiles, syncRulesWithParams } from './report/importBundle'
import type { JasperEmbedFile, JrxmlParameter } from '@jasper/embed'

const JasperWorkspace = lazy(() => import('@jasper/embed'))

const inputStyle: React.CSSProperties = {
  width: '100%', fontSize: 13, padding: '4px 8px', borderRadius: 6,
  border: '1px solid var(--color-border-strong)', background: 'var(--color-input-bg)',
  color: 'var(--color-text)',
}
const selectStyle: React.CSSProperties = { ...inputStyle, width: 'auto' }
/** Expand-all / collapse-all micro-buttons (mirrors the visual designer's). */
const expandBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 22, height: 22, padding: 0, border: '1px solid var(--color-border)',
  borderRadius: 5, background: 'var(--color-bg)', cursor: 'pointer',
  color: 'var(--color-text-secondary)',
}

export default function ReportDesignerPage({
  embedded = false,
  hostFullscreen = false,
  embedProject,
  embedObjName,
  onDirtyChange,
  registerSave,
  onSaved,
}: EmbeddedDesignerProps = {}) {
  const params = useParams<{ project: string; objName: string }>()
  const project = embedProject ?? params.project
  useRunOnlyRedirect(project)
  const objName = embedObjName ?? params.objName
  const navigate = useNavigate()
  const location = useLocation()
  const projectName = project || ''
  const objectName = objName || ''
  const { currentProject, selectProject } = useProjectStore()
  const theme = useThemeStore((s) => s.theme)

  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  const [model, setModel] = useState<ReportModel>(() => blankReportModel(objectName))
  const [jrxmlFiles, setJrxmlFiles] = useState<Record<string, string>>({})
  const [embedFiles, setEmbedFiles] = useState<JasperEmbedFile[]>([])
  const [jrxmlParams, setJrxmlParams] = useState<JrxmlParameter[]>([])
  const [projectVars, setProjectVars] = useState<ProjectVariable[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState<'design' | 'criteria' | 'options' | 'schedules'>('design')
  // Expand/collapse-ALL broadcast for the criteria cards (same contract as the
  // visual designer: each seq bump applies `open` to every mounted card).
  const [cardSignal, setCardSignal] = useState({ open: false, seq: 0 })
  const [embedFullscreen, setEmbedFullscreen] = useState(false)
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()

  const snapshot = useMemo(
    () => JSON.stringify({ model, jrxmlFiles }),
    [model, jrxmlFiles],
  )
  const isDirty = savedSnapshot !== null && snapshot !== savedSnapshot
  const { guard, showDiscardModal, confirmDiscard, cancelDiscard } =
    useUnsavedChangesGuard(isDirty)
  const agentRefresh = useAgentRefresh(projectName, isDirty)

  const embedApi = useMemo(() => makeReportEmbedApi(projectName), [projectName])

  // ── Load ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectName || !objectName) return
    let cancelled = false
    setLoading(true)
    loadReportObject(projectName, objectName)
      .then((data) => {
        if (cancelled) return
        const m = sanitizeReportModel(data.content, objectName)
        let files = data.jrxml_files || {}
        if (!Object.keys(files).length) {
          files = { [m.REPORT.MAIN_JRXML]: defaultReportJrxml(objectName) }
        }
        setModel(m)
        setJrxmlFiles(files)
        setEmbedFiles(
          Object.entries(files).map(([filename, content]) => ({
            filename, content, isMain: filename === m.REPORT.MAIN_JRXML,
          })),
        )
        // Seed criteria sync from the model's cached PARAMETERS until the
        // embed parses the live JRXML.
        setJrxmlParams(
          m.REPORT.PARAMETERS.map((p) => ({ name: p.name, javaClass: p.java_class })),
        )
        setSavedSnapshot(JSON.stringify({
          model: m,
          jrxmlFiles: files,
        }))
      })
      .catch((err) => {
        console.error('Failed to load report object', err)
        toast.error('Failed to load report object')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [projectName, objectName, agentRefresh])

  useEffect(() => {
    if (!projectName) { setProjectVars([]); return }
    listProjectVariables(projectName)
      .then(setProjectVars)
      .catch(() => setProjectVars([]))
  }, [projectName])

  // ── Model updates ─────────────────────────────────────────────
  const updateModel = useCallback((fn: (m: ReportModel) => void) => {
    setModel((prev) => {
      const next = JSON.parse(JSON.stringify(prev)) as ReportModel
      fn(next)
      return next
    })
  }, [])

  const rules = model.SQLMODEL.CRITERIA.query.rules
  const handleCriteriaChange = useCallback(
    (idx: number, field: keyof CriteriaRule, value: unknown) => {
      updateModel((m) => {
        const rule = m.SQLMODEL.CRITERIA.query.rules[idx]
        if (rule) (rule as Record<string, unknown>)[field as string] = value
      })
    },
    [updateModel],
  )
  const declaredNames = useMemo(
    () => new Set(jrxmlParams.map((p) => p.name)),
    [jrxmlParams],
  )

  // Keep criteria rows in lock-step with the JRXML's $P{} parameters.
  useEffect(() => {
    if (loading) return
    setModel((prev) => {
      const synced = syncRulesWithParams(prev.SQLMODEL.CRITERIA.query.rules, jrxmlParams)
      if (JSON.stringify(synced) === JSON.stringify(prev.SQLMODEL.CRITERIA.query.rules)) {
        return prev
      }
      const next = JSON.parse(JSON.stringify(prev)) as ReportModel
      next.SQLMODEL.CRITERIA.query.rules = synced
      return next
    })
  }, [jrxmlParams, loading])

  // ── Embed callbacks ───────────────────────────────────────────
  const handleFilesChange = useCallback((files: JasperEmbedFile[]) => {
    setJrxmlFiles(Object.fromEntries(files.map((f) => [f.filename, f.content])))
    const main = files.find((f) => f.isMain) ?? files[0]
    if (main) {
      updateModel((m) => {
        m.REPORT.MAIN_JRXML = main.filename
        m.REPORT.JRXML_FILES = files.map((f) => f.filename)
      })
    }
  }, [updateModel])

  const handleParametersChange = useCallback((params: JrxmlParameter[]) => {
    setJrxmlParams(params)
  }, [])

  // ── Import (.zip bundle / loose .jrxml) ───────────────────────
  // The embedded designer offers the same import in its own toolbar, but that
  // toolbar collapses to unlabelled icons below 600px — which is most of the
  // time inside the workbench's split pane. So the host offers it too, always
  // labelled, from the header beside Save. Handing the read files back as a
  // NEW `embedFiles` array re-seeds the designer's store (its `files` prop is
  // compared by identity); nothing is written until the user saves.
  const importInputRef = useRef<HTMLInputElement>(null)
  const handleImportPick = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || [])
    e.target.value = ''
    if (!picked.length) return
    if (isDirty && !window.confirm(
      'Importing replaces the report\u2019s JRXML files. Discard your unsaved changes?',
    )) return
    try {
      const read = await readPickedReportFiles(picked)
      const { files, main } = read
      setEmbedFiles(files)
      setJrxmlFiles(read.jrxmlFiles)
      updateModel((m) => {
        m.REPORT.MAIN_JRXML = main.filename
        m.REPORT.JRXML_FILES = files.map((f) => f.filename)
      })
      toast.success(
        files.length === 1
          ? `Imported ${main.filename} \u2014 save to keep it`
          : `Imported ${files.length} files (main: ${main.filename}) \u2014 save to keep them`,
      )
    } catch (err) {
      console.error('Report import failed', err)
      toast.error(err instanceof Error ? err.message : 'Failed to import report files')
    }
  }, [isDirty, updateModel])

  // ── Save ──────────────────────────────────────────────────────
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!projectName || !objectName) return false
    setSaving(true)
    try {
      const res = await saveReportObject(projectName, objectName, {
        model, jrxml_files: jrxmlFiles,
      })
      setSavedSnapshot(JSON.stringify({ model, jrxmlFiles }))
      for (const w of res.warnings || []) toast(w, { icon: '⚠️' })
      toast.success('Report saved')
      // The embedding shell gates its Live Preview tab on the model EXISTING,
      // a question it answered once when the node was selected. A report
      // authored or imported here is the first time that answer changes, so
      // say so — otherwise the tab stays missing until the node is reselected.
      onSaved?.()
      return true
    } catch (err) {
      console.error('Save failed', err)
      toast.error('Failed to save report')
      return false
    } finally {
      setSaving(false)
    }
  }, [projectName, objectName, model, jrxmlFiles, onSaved])

  useEffect(() => {
    if (!embedded || !onDirtyChange) return
    onDirtyChange(isDirty)
    return () => onDirtyChange(false)
  }, [embedded, isDirty, onDirtyChange])
  useEffect(() => {
    if (!embedded || !registerSave) return
    registerSave(handleSave)
  }, [embedded, registerSave, handleSave])

  const handleBack = useCallback(() => {
    const selectedNodeId = (location.state as { selectedNodeId?: string })?.selectedNodeId
    guard(() => navigate('/workbench', { state: { selectedNodeId } }))
  }, [guard, location.state, navigate])

  const handleSaveAndContinue = useCallback(async () => {
    const ok = await handleSave()
    if (ok) confirmDiscard()
  }, [handleSave, confirmDiscard])

  // ── Render ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: 32, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-secondary)' }}>
        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading report…
      </div>
    )
  }

  const paramNames = rules
    .map((r) => String(r.JASPER_PARAM || r.field || ''))
    .filter(Boolean)

  return (
    <div style={embedded && embedFullscreen ? {
      position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--color-bg)',
      display: 'flex', flexDirection: 'column', padding: '0 12px',
    } : {
      display: 'flex', flexDirection: 'column', height: '100%',
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '4px 0', flexShrink: 0, gap: 12,
      }}>
        <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
          {!embedded && (
            <button className="btn btn-ghost btn-sm" onClick={handleBack}>
              <ArrowLeft size={16} />
            </button>
          )}
          <FileText size={16} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
          <div style={{
            minWidth: 0, fontSize: '0.9125rem', color: 'var(--color-text)', fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {projectName}
            <span style={{ opacity: 0.4, margin: '0 6px' }}>/</span>
            {objectName}
            <span style={{
              marginLeft: 8, fontSize: '0.8125rem', fontWeight: 400,
              color: 'var(--color-text-secondary)', opacity: 0.8,
            }}>report</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <input
                ref={importInputRef}
                type="file"
                accept=".zip,.jrxml,.xml"
                multiple
                style={{ display: 'none' }}
                onChange={handleImportPick}
              />
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => importInputRef.current?.click()}
                title="Import a .zip bundle (main report + subreports) or .jrxml files"
              >
                <Upload size={14} />
                Import
              </button>
            </>
          )}
          {canEdit ? (
            <button
              className={`btn btn-sm ${isDirty ? 'btn-primary' : 'btn-secondary'}`}
              onClick={handleSave}
              disabled={saving}
              title={isDirty ? 'You have unsaved changes' : 'No changes to save'}
              style={isDirty ? { boxShadow: '0 0 0 3px rgba(76,141,255,0.35)' } : undefined}
            >
              {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
              {saving ? 'Saving…' : isDirty ? 'Save *' : 'Save'}
            </button>
          ) : (
            <ReadOnlyBadge />
          )}
          {embedded && !hostFullscreen && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setEmbedFullscreen((f) => !f)}
              title={embedFullscreen ? 'Restore' : 'Full screen'}
            >
              {embedFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 4,
        borderTop: '1px solid var(--color-border)',
        borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg-secondary)', padding: '0 8px',
        flexShrink: 0,
      }}>
        {([
          { key: 'design', label: 'Design' },
          { key: 'criteria', label: `Criteria${rules.length ? ` (${rules.length})` : ''}` },
          { key: 'options', label: 'Options' },
          // The DOOR onto scheduling: the schedulers that render this report
          // (derived, never stored here) and every render of it, on screen or
          // at 2 a.m. — one table, because they are the same kind of event.
          { key: 'schedules', label: 'Schedules & runs' },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '8px 14px', border: 'none', background: 'transparent',
              borderBottom: tab === t.key ? '2px solid var(--color-accent)' : '2px solid transparent',
              fontSize: 14, fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? 'var(--color-text)' : 'var(--color-text-secondary)',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
        {/* Design tab stays MOUNTED across tab switches (its editor holds
            cursor/undo state); the other tabs render above it. */}
        <div style={{
          position: 'absolute', inset: 0,
          visibility: tab === 'design' ? 'visible' : 'hidden',
        }}>
          <Suspense
            fallback={(
              <div style={{ padding: 32, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-secondary)' }}>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading designer…
              </div>
            )}
          >
            <JasperWorkspace
              files={embedFiles}
              onFilesChange={handleFilesChange}
              api={embedApi}
              themeMode={theme}
              tabs={['source', 'visual', 'query']}
              onParametersChange={handleParametersChange}
              allowBundleImport={canEdit}
            />
          </Suspense>
        </div>

        {tab === 'criteria' && (
          <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', background: 'var(--color-bg)', padding: 16 }}>
            {rules.length === 0 ? (
              <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', maxWidth: 560 }}>
                No criteria yet. Declare parameters in the JRXML
                (<code>{'<parameter name="FROM_DATE" class="java.sql.Date"/>'}</code>,
                referenced as <code>{'$P{FROM_DATE}'}</code> in the query) — each
                parameter automatically becomes a criteria row here, edited with
                the SAME card the visual designer uses: prompt heading, data
                type, operator, token-aware defaults, IN lists, fixed dropdowns,
                F2 lookup and item_change.
              </div>
            ) : (
              <div style={{ maxWidth: 1200 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
                  fontSize: 13, fontWeight: 700, letterSpacing: 0.5,
                  textTransform: 'uppercase', color: 'var(--color-text-secondary)',
                }}>
                  <span>Criteria ({rules.length})</span>
                  <span style={{ flexGrow: 1 }} />
                  <span style={{ display: 'inline-flex', gap: 4, flexShrink: 0 }}>
                    <button
                      type="button" style={expandBtnStyle} title="Expand all criteria"
                      onClick={() => setCardSignal((s) => ({ open: true, seq: s.seq + 1 }))}
                    >
                      <ChevronsUpDown size={13} />
                    </button>
                    <button
                      type="button" style={expandBtnStyle} title="Collapse all criteria"
                      onClick={() => setCardSignal((s) => ({ open: false, seq: s.seq + 1 }))}
                    >
                      <ChevronsDownUp size={13} />
                    </button>
                  </span>
                </div>
                {/* Same responsive card grid as the visual designer's criteria
                    drop-zone, so both designers read identically. */}
                <div style={{
                  display: 'grid', alignItems: 'start', gap: 12,
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                }}>
                  {rules.map((rule, idx) => {
                    const jasperName = String(rule.JASPER_PARAM || rule.field || '')
                    const orphan = !declaredNames.has(jasperName)
                    return (
                      <CriteriaCard
                        key={rule.id || jasperName || idx}
                        rule={rule}
                        idx={idx}
                        project={projectName}
                        allRules={rules}
                        projectVars={projectVars}
                        // A report criterion ALWAYS prompts — its value binds a
                        // JRXML $P{} parameter at run time, one rule to one
                        // parameter (report_params.fold_report_params).
                        promptOnly
                        fieldLocked
                        tone={orphan ? 'warn' : 'normal'}
                        headerLabel={`$P{${jasperName}}`}
                        headerBadge={orphan ? (
                          <span style={{ fontSize: 13, color: 'var(--color-warning, #b58900)', flexShrink: 0 }}>
                            not declared in the JRXML
                          </span>
                        ) : undefined}
                        lockedHint={(
                          <>Binds <code>{`$P{${jasperName}}`}</code> in the JRXML.</>
                        )}
                        // Jasper parameter names are CASE-SENSITIVE, so the
                        // lookup / item_change keys off the $P{} name verbatim
                        // rather than the canonical UPPER_SNAKE form.
                        triggerName={jasperName}
                        otherParamNames={paramNames.filter((n) => n !== jasperName)}
                        // Deployed as POPHELP.MSG_TITLE, a 20-character column.
                        lookupTitleMaxLength={20}
                        // The Criteria tab exists only for these, so they open
                        // expanded (collapse-all is one click away).
                        defaultOpen
                        expandSignal={cardSignal}
                        onChange={handleCriteriaChange}
                        // Only an orphan can be removed — a declared parameter's
                        // row is owned by the JRXML and comes straight back.
                        onDelete={orphan ? () => updateModel((m) => {
                          m.SQLMODEL.CRITERIA.query.rules =
                            m.SQLMODEL.CRITERIA.query.rules.filter(
                              (r) => String(r.JASPER_PARAM || r.field || '') !== jasperName,
                            )
                        }) : undefined}
                      />
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'schedules' && (
          <div style={{ height: '100%', overflow: 'auto', padding: '14px 16px' }}>
            <ObjectSchedulesPanel project={project || ''} objName={objName || ''} kind="report" />
          </div>
        )}

        {tab === 'options' && (
          <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', background: 'var(--color-bg)', padding: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 560 }}>
              <label style={{ fontSize: 13, color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                Title
                <input
                  style={inputStyle}
                  value={String(model.TITLE || '')}
                  onChange={(e) => updateModel((m) => { m.TITLE = e.target.value })}
                />
              </label>
              <label style={{ fontSize: 13, color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                Default output format
                <select
                  style={selectStyle}
                  value={model.REPORT.DEFAULT_OUTPUT_FORMAT}
                  onChange={(e) => updateModel((m) => {
                    m.REPORT.DEFAULT_OUTPUT_FORMAT = e.target.value as ReportOutputFormat
                  })}
                >
                  {REPORT_FORMAT_OPTIONS.map((f) => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                </select>
              </label>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                Allowed output formats
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {REPORT_FORMAT_OPTIONS.map((f) => (
                    <label key={f} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text)' }}>
                      <input
                        type="checkbox"
                        checked={model.REPORT.OUTPUT_FORMATS.includes(f)}
                        onChange={(e) => updateModel((m) => {
                          const set = new Set(m.REPORT.OUTPUT_FORMATS)
                          if (e.target.checked) set.add(f)
                          else set.delete(f)
                          m.REPORT.OUTPUT_FORMATS = REPORT_FORMAT_OPTIONS.filter((x) => set.has(x))
                        })}
                      />
                      {f.toUpperCase()}
                    </label>
                  ))}
                </div>
              </div>
              <label style={{ fontSize: 13, color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                External connection id (blank = project database)
                <input
                  style={inputStyle}
                  value={model.SQLMODEL.DATABASE_DETAIL.CONNECTION_ID}
                  onChange={(e) => updateModel((m) => {
                    m.SQLMODEL.DATABASE_DETAIL.CONNECTION_ID = e.target.value
                  })}
                  placeholder="e.g. CONN_ANALYTICS (Connections page)"
                />
              </label>
              {model.REPORT.PARAMETERS.length > 0 && (
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  Declared JRXML parameters:{' '}
                  {model.REPORT.PARAMETERS.map((p) => p.name).join(', ')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <DiscardChangesModal
        open={showDiscardModal}
        onSave={handleSaveAndContinue}
        onDiscard={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </div>
  )
}
